import { useState, useEffect, useRef } from "react";
import type { Unit } from "@/lib/types/unit";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogPortal,
  DialogOverlay,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Badge } from "@/components/ui/badge";
import { TraitBadgeList } from "@/components/ui/trait-badge-list";
import { TraitIcon } from "@/components/ui/trait-icon";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UnitStats } from "@/components/UnitDetail/UnitStats";
import { WeaponCard } from "@/components/UnitDetail/WeaponCard";
import { useWeaponByDescriptor } from "@/lib/store/data-store";
import { getUnitImagePath } from "@/lib/utils/unit-images";
import { getDivisionImagePath } from "@/lib/utils/division-images";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { X } from "lucide-react";
import { useComparisonModals } from "./ComparisonModalManager";
import type { Veterancy } from "@/lib/types/veterancy-cohesion";
import { getTraitModifiersMap, getVeterancyDisplayName } from "@/lib/types/veterancy-cohesion";
import { getTraitDisplayName, getTraitDescription } from "@/lib/utils/trait-names";
import { getTraitEffectDescriptions } from "@/lib/damage-calculator/trait-effect-descriptions";
import { useUnitTraitState } from "@/lib/hooks/useUnitTraitState";

function getNationFlagPath(country: string): string {
  if (!country) return "";
  return `/images/nations/${country}.png`;
}

interface UnitInfoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  unit: Unit;
  comparisonMode?: boolean;
  position?: { x: number; y: number };
  id?: string;
  zIndex?: number;
  onClose?: () => void;
}

// Hook to detect if we're on mobile (< lg breakpoint = 1024px)
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 1024;
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return isMobile;
}

// Shared content component to avoid duplication
function UnitInfoContent({ unit }: { unit: Unit }) {
  const [imageError, setImageError] = useState(false);
  const [activeWeaponIndex, setActiveWeaponIndex] = useState(0);
  const weaponData = useWeaponByDescriptor(unit?.weaponDescriptorName || "");

  // Use shared trait state hook
  const {
    traitOverrides,
    veterancy,
    setVeterancy,
    traitsWithModifiers,
    traitActivationStates,
    modifiedStats,
    terrainSpeeds,
    veterancyOptions,
    handleTraitToggle,
    loadingUnitTraitEffects,
  } = useUnitTraitState(unit);

  useEffect(() => {
    if (weaponData?.weapons && weaponData.weapons.length > 0) {
      setActiveWeaponIndex(0);
    }
  }, [weaponData?.weapons]);

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        {/* Unit Image and Specialities */}
        <div className="flex flex-col gap-4">
          {/* Unit Image */}
          {!imageError ? (
            <img
              src={getUnitImagePath(unit.descriptorName)}
              alt={unit.name}
              className="w-full h-auto max-h-[250px] object-contain bg-muted rounded-lg border-2 border-muted-foreground/20"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-full min-h-[200px] max-h-[250px] bg-muted rounded-lg flex items-center justify-center border-2 border-dashed border-muted-foreground/20">
              <span className="text-sm text-muted-foreground">Unit Image</span>
            </div>
          )}

          {/* Specialities */}
          {unit.specialities && unit.specialities.length > 0 && (
            <div>
              <TraitBadgeList specialities={unit.specialities} />
            </div>
          )}
        </div>

        {/* Key Stats */}
        <div className="flex flex-col justify-between">
          <div>
            {/* Command Points and Health */}
            <div className="grid grid-cols-2 gap-2 mb-4">
              <div>
                <div className="text-sm text-muted-foreground mb-1">CP</div>
                <div className="text-2xl font-bold">{unit.commandPoints}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">Health</div>
                <div className="text-2xl font-bold">{unit.maxDamage}</div>
              </div>
            </div>

            {/* Armor values */}
            <div className="mb-4 pt-2 border-t">
              <div className="grid grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-sm text-muted-foreground mb-1">Front</div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="text-2xl font-bold cursor-help inline-block">{unit.frontArmor}</div>
                    </TooltipTrigger>
                    <TooltipContent className="z-[100]">
                      <p>{unit.frontArmorType}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className="text-center">
                  <div className="text-sm text-muted-foreground mb-1">Side</div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="text-2xl font-bold cursor-help inline-block">{unit.sideArmor}</div>
                    </TooltipTrigger>
                    <TooltipContent className="z-[100]">
                      <p>{unit.sideArmorType}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className="text-center">
                  <div className="text-sm text-muted-foreground mb-1">Rear</div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="text-2xl font-bold cursor-help inline-block">{unit.rearArmor}</div>
                    </TooltipTrigger>
                    <TooltipContent className="z-[100]">
                      <p>{unit.rearArmorType}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className="text-center">
                  <div className="text-sm text-muted-foreground mb-1">Top</div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="text-2xl font-bold cursor-help inline-block">{unit.topArmor}</div>
                    </TooltipTrigger>
                    <TooltipContent className="z-[100]">
                      <p>{unit.topArmorType}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Trait Toggles and Veterancy Configuration */}
      {(traitsWithModifiers.length > 0 || veterancyOptions.length > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 mt-3 pt-3 border-t">
          {traitsWithModifiers.length > 0 && (
            <div>
              <div className="mb-1.5 text-xs font-semibold text-muted-foreground">Trait Effects</div>
              <div className="flex flex-wrap gap-1.5">
                {traitsWithModifiers.map((traitName) => {
                  let modifier;
                  let effects: string[] = [];
                  try {
                    const traitModifiersMap = getTraitModifiersMap();
                    modifier = traitModifiersMap[traitName];
                    effects = modifier ? getTraitEffectDescriptions(traitName, modifier) : [];
                  } catch {
                    // Trait modifiers map not initialized yet
                  }
                  const isEnabled = traitActivationStates.get(traitName) ?? false;
                  const displayName = getTraitDisplayName(traitName);
                  const description = getTraitDescription(traitName);

                  return (
                    <Tooltip key={traitName}>
                      <TooltipTrigger asChild>
                        <div 
                          className={`inline-flex items-center gap-1.5 px-2 py-1.5 rounded-md border cursor-pointer transition-all ${
                            isEnabled 
                              ? "bg-primary/10 border-primary/40" 
                              : "bg-muted/30 border-muted-foreground/20"
                          }`}
                          onClick={() => handleTraitToggle(traitName)}
                        >
                          <TraitIcon speciality={traitName} size="sm" showText={false} showTooltip={false} />
                          <span className="text-xs font-medium whitespace-nowrap hidden md:inline">
                            {displayName}
                          </span>
                          <Switch
                            checked={isEnabled}
                            onCheckedChange={() => handleTraitToggle(traitName)}
                            className="h-4 w-7 data-[state=checked]:bg-primary"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-sm z-[100]" side="top">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between gap-2 pb-2 border-b">
                            <div className="font-semibold">{displayName}</div>
                            <Badge variant={isEnabled ? "default" : "secondary"} className="text-xs">
                              {isEnabled ? "Active" : "Inactive"}
                            </Badge>
                          </div>
                          <div className="text-sm">{description}</div>
                          {effects.length > 0 && (
                            <div className="text-xs pt-2 border-t">
                              <div className="font-medium mb-1">Effects:</div>
                              <ul className="list-disc list-inside space-y-0.5">
                                {effects.map((effect, i) => (
                                  <li key={i}>{effect}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {traitName === '_sf' && (
                            <div className="text-xs text-blue-500 pt-2 border-t flex items-center gap-1">
                              <span>ℹ️</span>
                              <span className="font-medium">Display Only:</span>
                              <span>This trait cannot be toggled for gameplay effect. Special Forces bonuses are automatically applied through veterancy.</span>
                            </div>
                          )}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            </div>
          )}
          {veterancyOptions.length > 0 && (
            <div className="sm:min-w-[160px]">
              <div className="text-xs font-semibold text-muted-foreground mb-1.5">Veterancy</div>
              <Select
                value={veterancy || "none"}
                onValueChange={(value) => {
                  if (value === "none") {
                    setVeterancy(undefined);
                  } else {
                    setVeterancy(value as Veterancy);
                  }
                }}
              >
                <SelectTrigger className="w-full h-7 text-xs py-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {veterancyOptions.map((vet) => (
                    <SelectItem key={vet} value={vet}>
                      {getVeterancyDisplayName(vet)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}

      {/* Unit Stats */}
      <div className="mt-4">
        <UnitStats
          speed={unit.speed}
          roadSpeed={unit.roadSpeed}
          rotationTime={unit.rotationTime}
          optics={unit.optics}
          airOptics={unit.airOptics}
          lowAltOptics={unit.lowAltOptics}
          opticsRange={unit.opticsRange}
          airOpticsRange={unit.airOpticsRange}
          lowAltOpticsRange={unit.lowAltOpticsRange}
          stealth={unit.stealth}
          ecm={unit.ecm}
          fuel={unit.fuel}
          fuelMove={unit.fuelMove}
          supply={unit.supply}
          advancedDeployment={unit.advancedDeployment}
          maxDamage={unit.maxDamage}
          dangerousness={unit.dangerousness}
          agility={unit.agility}
          movementType={unit.movementType}
          flyingAltitude={unit.flyingAltitude}
          travelTime={unit.travelTime}
          bombStrategy={unit.bombStrategy}
          modifiedStats={modifiedStats}
          terrainSpeeds={terrainSpeeds}
        />
      </div>

      {/* Weapons */}
      {weaponData?.weapons && weaponData.weapons.length > 0 && (
        <div className="mt-4">
          <div className="text-sm font-semibold text-muted-foreground mb-2">Weapons</div>
          
          {/* Tabs */}
          <div className="mb-4">
            <div className="flex gap-2 border-b overflow-x-auto">
              {weaponData.weapons.map((weapon, index) => (
                <Button
                  key={weapon.salvoIndex}
                  variant={activeWeaponIndex === index ? "default" : "ghost"}
                  onClick={() => setActiveWeaponIndex(index)}
                  className="rounded-b-none whitespace-nowrap text-xs h-8"
                  size="sm"
                >
                  {weapon.ammo.name} {weapon.numberOfWeapons > 1 && `x${weapon.numberOfWeapons}`}
                </Button>
              ))}
            </div>
          </div>

          {/* Tab content */}
          {weaponData.weapons[activeWeaponIndex] && (
            <WeaponCard 
              weapon={weaponData.weapons[activeWeaponIndex]} 
              weaponIndex={activeWeaponIndex}
              traitOverrides={traitOverrides}
              veterancy={veterancy}
              unit={unit}
            />
          )}
        </div>
      )}
    </>
  );
}

export function UnitInfoDialog({ 
  open, 
  onOpenChange, 
  unit,
  comparisonMode = false,
  position,
  id,
  zIndex = 50,
  onClose,
}: UnitInfoDialogProps) {
  const isMobile = useIsMobile();

  if (!unit) return null;

  // Comparison mode: Render as non-modal positioned dialog (desktop only)
  if (comparisonMode && !isMobile) {
    const { updateModalPosition } = useComparisonModals();
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [currentPosition, setCurrentPosition] = useState(() => {
      // Initialize with position prop if provided, otherwise use default
      return position || { x: 0, y: 0 };
    });
    const dialogRef = useRef<HTMLDivElement>(null);

    const handleClose = () => {
      if (onClose) {
        onClose();
      } else {
        onOpenChange(false);
      }
    };

    const handleMouseDown = (e: React.MouseEvent) => {
      // Only start drag on left mouse button and not on close button
      if (e.button !== 0 || (e.target as HTMLElement).closest('button')) return;
      
      if (dialogRef.current) {
        const rect = dialogRef.current.getBoundingClientRect();
        setDragOffset({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        });
        setIsDragging(true);
        e.preventDefault();
      }
    };

    useEffect(() => {
      if (!isDragging) return;

      const handleMouseMove = (e: MouseEvent) => {
        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;
        setCurrentPosition({ x: newX, y: newY });
      };

      const handleMouseUp = (e: MouseEvent) => {
        setIsDragging(false);
        // Update position in manager when drag ends
        if (id) {
          const finalX = e.clientX - dragOffset.x;
          const finalY = e.clientY - dragOffset.y;
          updateModalPosition(id, { x: finalX, y: finalY });
        }
      };

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);

      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }, [isDragging, dragOffset, id, updateModalPosition]);

    // Update currentPosition when position prop changes (from manager)
    // Skip during dragging to allow smooth drag following
    useEffect(() => {
      if (isDragging) return; // Don't sync during drag
      if (position && (position.x !== currentPosition.x || position.y !== currentPosition.y)) {
        setCurrentPosition(position);
      }
    }, [position, isDragging, currentPosition.x, currentPosition.y]);

    return (
      <Dialog open={open} onOpenChange={handleClose} modal={false}>
        <DialogPortal>
          {/* No overlay in comparison mode */}
          <DialogPrimitive.Content
            ref={dialogRef}
            className={cn(
              "fixed z-50 grid w-full max-w-md max-h-[85vh] overflow-y-auto gap-4 rounded-lg border bg-background p-4 shadow-lg select-none",
              isDragging ? "" : "duration-200"
            )}
            style={{
              left: `${currentPosition.x}px`,
              top: `${currentPosition.y}px`,
              transform: "none",
              zIndex: 100,
              willChange: isDragging ? "left, top" : "auto",
              transition: isDragging ? "none" : undefined,
            }}
            onInteractOutside={(e) => {
              // Prevent closing on outside click in comparison mode
              e.preventDefault();
            }}
          >
            <DialogHeader>
              <div 
                className="flex items-center gap-3 flex-wrap mb-2 cursor-grab active:cursor-grabbing"
                onMouseDown={handleMouseDown}
              >
                <DialogTitle className="text-xl font-bold flex-1">{unit.name}</DialogTitle>
                <Badge variant="outline" className="p-0 w-10 h-6 rounded-md">
                  <img
                    src={getNationFlagPath(unit.unitType.country)}
                    alt={unit.unitType.country}
                    className="w-full h-full object-cover"
                  />
                </Badge>
                {unit.isCommand && <Badge variant="secondary">Command</Badge>}
                {unit.isSpecialForces && <Badge variant="secondary">Special Forces</Badge>}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClose();
                  }}
                  className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none ml-auto"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </button>
              </div>
              {unit.divisions && unit.divisions.length > 0 ? (
                <div className="flex items-center gap-1.5 flex-wrap">
                  {unit.divisions.map((divisionDescriptor) => (
                    <Tooltip key={divisionDescriptor}>
                      <TooltipTrigger asChild>
                        <div className="w-6 h-6 cursor-help flex-shrink-0">
                          <img
                            src={getDivisionImagePath(divisionDescriptor)}
                            alt={divisionDescriptor}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="z-[100]">
                        <p>{divisionDescriptor}</p>
                      </TooltipContent>
                    </Tooltip>
                  ))}
                </div>
              ) : null}
            </DialogHeader>
            <UnitInfoContent unit={unit} />
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>
    );
  }

  // Mobile: Use Sheet component (bottom slide-up).
  // Default overlay/content z (53/54) sits above app Dialog (z-[52] in dialog.tsx) so nested
  // sheets—e.g. transport info from Add Unit—are not trapped behind the parent modal.
  if (isMobile) {
    const overlayZ = zIndex > 50 ? zIndex : 53;
    const contentZ = zIndex > 50 ? zIndex + 1 : 54;
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          className="w-full max-h-[90vh] overflow-y-auto p-0 rounded-t-lg"
          showCloseButton={false}
          style={{ zIndex: contentZ }}
          overlayStyle={{ zIndex: overlayZ }}
        >
          {/* Swipe indicator handle */}
          <div className="flex justify-center pt-2 pb-1">
            <div className="w-12 h-1 bg-muted-foreground/30 rounded-full" />
          </div>
          <SheetHeader className="border-b sticky top-0 z-20 bg-background px-4 pb-4 pt-[max(0.5rem,env(safe-area-inset-top,0px))] sm:px-6 sm:pb-4 sm:pt-[max(1rem,env(safe-area-inset-top,0px))] relative">
            {/* Close button - positioned absolutely within header */}
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="absolute top-[calc(1rem+env(safe-area-inset-top,0px))] right-4 z-30 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </button>
            <div className="flex items-center gap-3 flex-wrap pr-10">
              <SheetTitle className="text-xl sm:text-2xl font-bold flex-1">{unit.name}</SheetTitle>
              <Badge variant="outline" className="p-0 w-10 h-6 rounded-md">
                <img
                  src={getNationFlagPath(unit.unitType.country)}
                  alt={unit.unitType.country}
                  className="w-full h-full object-cover"
                />
              </Badge>
              {unit.isCommand && <Badge variant="secondary">Command</Badge>}
              {unit.isSpecialForces && <Badge variant="secondary">Special Forces</Badge>}
            </div>
            {unit.divisions && unit.divisions.length > 0 ? (
              <div className="flex items-center gap-1.5 flex-wrap">
                {unit.divisions.map((divisionDescriptor) => (
                  <Tooltip key={divisionDescriptor}>
                    <TooltipTrigger asChild>
                      <div className="w-6 h-6 cursor-help flex-shrink-0">
                        <img
                          src={getDivisionImagePath(divisionDescriptor)}
                          alt={divisionDescriptor}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="z-[100]">
                      <p>{divisionDescriptor}</p>
                    </TooltipContent>
                  </Tooltip>
                ))}
              </div>
            ) : null}
          </SheetHeader>
          <div className="px-4 sm:px-6 pb-4 sm:pb-6 overflow-y-auto">
            <UnitInfoContent unit={unit} />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // Desktop: Use Dialog component (centered modal)
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3 flex-wrap mb-2">
            <DialogTitle className="text-2xl font-bold flex-1">{unit.name}</DialogTitle>
            <Badge variant="outline" className="p-0 w-10 h-6 rounded-md">
              <img
                src={getNationFlagPath(unit.unitType.country)}
                alt={unit.unitType.country}
                className="w-full h-full object-cover"
              />
            </Badge>
            {unit.isCommand && <Badge variant="secondary">Command</Badge>}
            {unit.isSpecialForces && <Badge variant="secondary">Special Forces</Badge>}
          </div>
          {unit.divisions && unit.divisions.length > 0 ? (
            <div className="flex items-center gap-1.5 flex-wrap">
              {unit.divisions.map((divisionDescriptor) => (
                <Tooltip key={divisionDescriptor}>
                  <TooltipTrigger asChild>
                    <div className="w-6 h-6 cursor-help flex-shrink-0">
                      <img
                        src={getDivisionImagePath(divisionDescriptor)}
                        alt={divisionDescriptor}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="z-[100]">
                    <p>{divisionDescriptor}</p>
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          ) : null}
        </DialogHeader>
        <UnitInfoContent unit={unit} />
      </DialogContent>
    </Dialog>
  );
}

